<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      mediaFile: null,
      mediaPreviewUrl: '',
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {},
  },
  computed: {
    isMediaAnImage() {
      return !!this.mediaFile && this.mediaFile.type.startsWith('image/');
    },
    isMediaAnAudio() {
      return !!this.mediaFile && this.mediaFile.type.startsWith('audio/');
    },
    hasNoContentOrMedia() {
      return !this.content && !this.mediaFile;
    },
  },
  methods: {
    onMediaFileChange(event) {
      const [file] = event.target.files;
      if (!file) return;
      this.mediaFile = file;
      this.mediaPreviewUrl = URL.createObjectURL(file);
    },
    removeMediaFile() {
      this.mediaFile = null;
      this.mediaPreviewUrl = '';
      this.$refs.mediaFileInput.value = '';
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.removeMediaFile();
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    addCannedResponse() {
      if (this.hasNoContentOrMedia) {
        useAlert(this.$t('CANNED_MGMT.ADD.FORM.CONTENT.EMPTY_ERROR'));
        return;
      }
      // Show loading on button
      this.addCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          ...(this.mediaFile && { media_file: this.mediaFile }),
          isFormData: !!this.mediaFile,
        })
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.MEDIA.LABEL') }}
          </label>
          <div v-if="!mediaFile" class="flex items-center gap-2">
            <input
              ref="mediaFileInput"
              type="file"
              accept="image/*,audio/*"
              class="hidden"
              @change="onMediaFileChange"
            />
            <NextButton
              type="button"
              faded
              slate
              icon="i-lucide-paperclip"
              :label="$t('CANNED_MGMT.ADD.FORM.MEDIA.ATTACH_BUTTON')"
              @click="$refs.mediaFileInput.click()"
            />
          </div>
          <div v-else class="flex items-center gap-3 mt-1">
            <img
              v-if="isMediaAnImage"
              :src="mediaPreviewUrl"
              class="object-cover w-16 h-16 rounded-md"
            />
            <audio
              v-else-if="isMediaAnAudio"
              :src="mediaPreviewUrl"
              controls
              class="h-10 max-w-full"
            />
            <NextButton
              type="button"
              faded
              ruby
              icon="i-lucide-x"
              size="sm"
              :label="$t('CANNED_MGMT.ADD.FORM.MEDIA.REMOVE_BUTTON')"
              @click="removeMediaFile"
            />
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.shortCode.$invalid ||
              hasNoContentOrMedia ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
